import { css } from "@compiled/react";
import { FormattedMessage } from "react-intl";

import { media } from "@atlaskit/primitives";

import { Adg4Button } from "@/components/adg4/button/src";
import { Picture } from "@/components/wpl/image/src/index";
import { useLocalizedHref } from "@/libraries/web-framework";

const buttonStyle = css({
    borderRadius: "4px",
    margin: "0 auto 24px auto"
});
const officialContainerCss = css({
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    textAlign: "center",
    position: "relative"
});

const headerCss = css({
    fontSize: "32px",
    lineHeight: "38px",
    fontWeight: 800,
    margin: "0 auto 14px auto",
    maxWidth: "480px",
    [media.above.sm]: {
        fontSize: "48px",
        lineHeight: "48px",
        maxWidth: "768px",
        margin: "0 auto 16px auto"
    }
});

const subtitleCss = css({
    fontSize: "16px",
    lineHeight: "24px",
    fontWeight: 400,
    margin: "0 auto 10px auto",
    maxWidth: "480px",
    [media.only.sm]: {
        fontSize: "20px",
        lineHeight: "28px",
        margin: "0 auto 20px"
    },
    [media.above.md]: {
        fontSize: "28px",
        lineHeight: "40px",
        maxWidth: "768px",
        margin: "0 auto 20px"
    }
});

const rowWrapperCss = css({
    "display": "flex",
    "flexWrap": "wrap",
    "justifyContent": "space-between",
    "listStyleType": "none",
    "padding": "0",
    "margin": 0,
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "& > li": {
        width: "50%",
        boxSizing: "border-box",
        padding: "16px",
        [media.above.sm]: {
            width: "25%"
        }
    }
});

const cardTitleCss = css({
    fontSize: "20px",
    lineHeight: "28px",
    margin: "0 0 12px"
});

const imageCss = css({
    maxWidth: "66px",
    maxHeight: "55px",
    position: "absolute",
    insetInlineEnd: "-8%",
    insetBlockEnd: "96%",
    [media.only.sm]: {
        maxWidth: "110px",
        maxHeight: "92px",
        insetInlineEnd: "5%",
        insetBlockEnd: "90%",
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        ["img"]: {
            width: "75%",
            height: "75%"
        }
    },
    [media.above.md]: {
        maxWidth: "180px",
        maxHeight: "148px",
        insetInlineEnd: "5%",
        insetBlockEnd: "75%",
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        ["img"]: {
            width: "100%",
            height: "100%"
        }
    }
});

export const OfficialSection = () => {
    const ctaButtonUrl = "/software/views/community-license-request#/";

    return (
        <div css={officialContainerCss}>
            <h2 css={headerCss}>
                <FormattedMessage
                    id="NonprofitPage.official.header"
                    defaultMessage="Let’s make it official"
                />
            </h2>
            <h3 css={subtitleCss}>
                <FormattedMessage
                    id="NonprofitPage.official.subtitle"
                    defaultMessage="Apply to verify your eligibility and receive your discounted Community License. Here’s how it’ll work."
                />
            </h3>
            <Adg4Button
                css={buttonStyle}
                variant="primary"
                size="small"
                color="blue"
                href={useLocalizedHref(ctaButtonUrl)}
            >
                <FormattedMessage
                    id="NonprofitPage.hero.button"
                    defaultMessage="Apply now"
                />
            </Adg4Button>
            <ul css={rowWrapperCss}>
                <li>
                    <Picture
                        image={{
                            src: "https://dam-cdn.atl.orangelogic.com/AssetLink/1665m6o0xxrphegnh7lnwf17585pj211.svg",
                            alt: ""
                        }}
                    />
                    <h4 css={cardTitleCss}>
                        <FormattedMessage
                            id="NonprofitPage.official.card.title1.v2"
                            defaultMessage="1. Choose your apps, calculate pricing, and sign up for a free plan or trial"
                        />
                    </h4>
                </li>
                <li>
                    <Picture
                        image={{
                            src: "https://dam-cdn.atl.orangelogic.com/AssetLink/ok813kjv073y16g445ogp8yerkv6qvc2.svg",
                            alt: ""
                        }}
                    />
                    <h4 css={cardTitleCss}>
                        <FormattedMessage
                            id="NonprofitPage.official.card.title2"
                            defaultMessage="2. Apply for your discounted license"
                        />
                    </h4>
                </li>
                <li>
                    <Picture
                        image={{
                            src: "https://dam-cdn.atl.orangelogic.com/AssetLink/h1503x520p8bg7w744h810188q4bypf3.svg",
                            alt: ""
                        }}
                    />
                    <h4 css={cardTitleCss}>
                        <FormattedMessage
                            id="NonprofitPage.official.card.title3"
                            defaultMessage="3. In 2-3 days, verification approved and received!"
                        />
                    </h4>
                </li>
                <li>
                    <Picture
                        image={{
                            src: "https://dam-cdn.atl.orangelogic.com/AssetLink/pe077p727atw2c04p00h3ecl1on0p841.svg",
                            alt: ""
                        }}
                    />
                    <h4 css={cardTitleCss}>
                        <FormattedMessage
                            id="NonprofitPage.official.card.title4"
                            defaultMessage="4. Discount automatically applied to your subscription and any future upgrades"
                        />
                    </h4>
                </li>
            </ul>
            <div css={imageCss}>
                <Picture
                    image={{
                        src: "https://dam-cdn.atl.orangelogic.com/AssetLink/g626i0m4331tsgn2313fln510p5578n1.svg",
                        alt: ""
                    }}
                />
            </div>
        </div>
    );
};
OfficialSection.displayName = "OfficialSection";
